import React, { useEffect, useMemo } from 'react'
import config from 'constants/config'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import moment from 'moment'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import getBedbankPromoExtraValue, { getDisplayTotalPricePerNight, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BusinessTravellerOfferCreditsTextLinkSwitch from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLinkSwitch'
import Group from 'components/utils/Group'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import { pluralizeToString } from 'lib/string/pluralize'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import PayLaterPill from 'components/Common/PayLaterPill'
import { safeDivideAndCeil } from 'lib/maths/mathUtils'

interface Props {
  rate?: App.BedbankRate;
  bundleRate?: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer;
  flightOrigin?: App.Airport;
  flightPrice?: number;
  duration: number;
  checkIn?: string;
  departureTooEarly?: boolean;
  flightsEnabled: boolean;
  onViewPackageOptions?: () => void;
  promotion?: boolean
  alignment?: 'center' | 'start'
  luxPlusLPPEnabled: boolean
  priceTrackedRef?: React.RefObject<boolean>
}

function BedbankSummaryPriceDetails(props: Props) {
  const {
    rate,
    bundleRate,
    rooms,
    offer,
    checkIn,
    flightPrice,
    flightsEnabled,
    duration,
    onViewPackageOptions,
    promotion = false,
    alignment = 'center',
    luxPlusLPPEnabled,
    priceTrackedRef,
  } = props

  const appDispatch = useAppDispatch()
  const hasFlights = !!flightPrice
  const sell = useBedbankOfferSell(offer, duration, rooms)

  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration])
  const promo = useBedbankRoomRatePromotions(offer.promotions, checkIn, checkOut)

  const taxesAndFees = rate?.totals?.taxesAndFees ?? sell?.taxesAndFees ?? 0
  const propertyFees = rate?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

  const hotelPrice = rate ? rate.totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = rate?.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  let hotelValue: undefined | number
  let bundleValue: undefined | number

  let hotelDiscount: number
  let hotelMemberDiscount: number = 0

  if (rate) {
    const pkg = rate.room
    const isPromoEnabledRate = !!promo[rate.promotionId ?? '']

    if (isPromoEnabledRate) {
      const inclusionsAmount = pkg?.sell ? getBedbankPromoExtraValue({
        price: pkg.sell.price,
        value: pkg.sell.value,
        minLos: pkg.sell.los,
        duration,
      }) : 0
      hotelValue = inclusionsAmount + rate.totals.inclusive
      bundleValue = inclusionsAmount + (bundleRate?.totals.inclusive || hotelValue)
      hotelDiscount = getValueOffPercent(hotelValue, inclusionsAmount)
    } else {
      hotelValue = rate.value
      bundleValue = bundleRate?.value || hotelValue
      hotelDiscount = rate.discount
      hotelMemberDiscount = rate.memberDiscount
    }
  } else {
    hotelValue = sell?.value
    bundleValue = sell?.value
    hotelDiscount = sell?.discountPercentage ?? 0
  }

  const hotelNights = rate?.nights ?? sell?.los

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const baseTotalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    hotelDiscount,
    hotelMemberDiscount,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const totalPricesPerNight = getDisplayTotalPricePerNight(baseTotalPrices, hotelNights || 1)
  const showOneNightPrice = !promotion && !offer.displayPricingAsPerPerson && !checkIn && !!hotelNights && hotelNights > 1
  const totalPrices = showOneNightPrice ? totalPricesPerNight : baseTotalPrices
  const nightsToDisplay = showOneNightPrice ? 1 : hotelNights

  const offerLocations: Array<string> = useMemo(() => {
    const locations = [
      offer.property?.address.city,
      offer.property?.address.stateProvinceName || '',
      offer.property?.address.countryName]
      .filter(Boolean)

    return locations
  }, [offer])

  const discount = promoteAsBundle ? totalPrices.original.discountWithFlight : totalPrices.original.discount
  const memberDiscount = promoteAsBundle ? totalPrices.original.memberDiscountWithFlight : totalPrices.original.memberDiscount
  const displayDiscount = useBedbankDiscount(discount, memberDiscount)

  useEffect(() => {
    appDispatch(setCustomerValue(totalPrices.totals.price))
  }, [appDispatch, totalPrices.totals.price])
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorBedbankOptions(rate, {
      price: totalPrices.totals.price,
      memberPrice: totalPrices.totals.memberPrice,
      propertyFees: showOneNightPrice ? safeDivideAndCeil(propertyFees, hotelNights || 1) : propertyFees,
      propertyId: offer.property.id,
      offerId: offer.id,
    }),
  ], [rate, totalPrices.totals.price, totalPrices.totals.memberPrice, showOneNightPrice, propertyFees, hotelNights, offer])

  const showHotelPriceWithFlights = !!flightsEnabled && hasFlights && !offer.promoteAsBundle
  const priceToTrack = showHotelPriceWithFlights ?
    totalPrices?.totals?.price :
    promoteAsBundle ? totalPrices?.totals?.flightBundlePrice : totalPrices?.totals?.price

  const shouldTrackPrice = !!(showHotelPriceWithFlights ? totalPrices?.totals?.price : totalPrices?.totals?.flightBundlePrice || totalPrices?.totals?.price)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && !!priceToTrack && searchId && offer?.id && priceTrackedRef && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: priceToTrack.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, priceToTrack, offer?.id])

  const canPayLater = !!rate?.paymentScheduleInfo?.supplierPaymentNumberOfDays

  if (showHotelPriceWithFlights) {
    return <Group direction="vertical" fullWidth gap={16} horizontalAlign="center">
      <Group direction="horizontal" gap={4} fullWidth horizontalAlign="start">
        {canPayLater && <PayLaterPill />}
        {displayDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
          type="estimate"
          discountPercentage={displayDiscount}
        />}
      </Group>
      <AccommodationPackageTilePricingBlocks>
        <Group direction="vertical" gap={12} verticalAlign="space-between">
          <Group gap={8} direction="vertical" verticalAlign="space-evenly">
            <Group direction="vertical" gap={12}>
              <BodyText variant="large" weight="bold">Hotel</BodyText>
              <Group direction="vertical">
                <Group direction="vertical" gap={4}>
                  <Group direction="vertical">
                    {!!nightsToDisplay && <PriceRowPriceCaption>
                      {pluralizeToString('night', nightsToDisplay)} from
                    </PriceRowPriceCaption>}
                    <LuxPlusPriceGuard
                      showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                      fallbackPrice={<PriceRowPrice
                        data-testid="bedbank-hotel-flights-summary-price"
                        size="L"
                        price={totalPrices.totals.price}
                        saleUnit={saleUnit}
                      />}
                    >
                      <LuxPlusPriceStack
                        size="S"
                        price={totalPrices.totals.price}
                        memberPrice={totalPrices.totals.memberPrice}
                        saleUnit={saleUnit}
                      />
                    </LuxPlusPriceGuard>
                    {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
                  </Group>
                  <BusinessTravellerOfferCreditsTextLinkSwitch
                    type="exact"
                    offer={offer}
                    totalCost={baseTotalPrices.original.price}
                    numberOfNights={hotelNights}
                    roomCount={rooms.length}
                  />
                </Group>
                {taxesAndFees > 0 && <PriceRowTaxesAndFees
                  endIcon={<OfferPriceDetailsRow
                    offerPage
                    offer={offer}
                    rooms={rooms}
                    propertyFees={propertyFees}
                    hotelPrice={hotelPrice}
                    hotelMemberPrice={hotelMemberPrice}
                    taxesAndFees={taxesAndFees}
                    flightPrice={flightPrice}
                    showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                    duration={hotelNights ?? 0}
                    trigger="info-icon"
                    cta={<TextButton kind="primary" fit="flex">
                      View {config.brandConfig.travelSynonym} options
                    </TextButton>}
                    onCtaAfterClose={onViewPackageOptions}
                  />}
                />}
                <PriceRowAgentHubCommission
                  size="L"
                  productType={offer.productType}
                  offerLocations={offerLocations}
                  offerId={offer.id}
                />
                {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
                  size="L"
                  originalValue={totalPrices.totals.value}
                />}
              </Group>
            </Group>
          </Group>
          <TextButton
            kind="secondary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            View options
          </TextButton>
        </Group>
        <Group direction="vertical" gap={12} verticalAlign="space-between">
          <Group gap={8} direction="vertical" verticalAlign="space-evenly">
            <Group direction="vertical" gap={12} verticalAlign="space-evenly">
              <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">Hotel + flights</BodyTextBlock>
              <Group direction="vertical">
                <Group direction="vertical" gap={4}>
                  <Group direction="vertical">
                    {!!nightsToDisplay && <PriceRowPriceCaption>
                      {pluralizeToString('night', nightsToDisplay)} + flights from
                    </PriceRowPriceCaption>}
                    <LuxPlusPriceGuard
                      showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                      fallbackPrice={<PriceRowPrice
                        data-testid="bedbank-hotel-flights-summary-price"
                        size="L"
                        price={totalPrices.totals.flightBundlePrice}
                        saleUnit={saleUnit}
                      />}
                    >
                      <LuxPlusPriceStack
                        size="S"
                        price={totalPrices.totals.flightBundlePrice}
                        memberPrice={totalPrices.totals.flightBundleMemberPrice}
                        saleUnit={saleUnit}
                      />
                    </LuxPlusPriceGuard>
                    {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
                  </Group>
                  <BusinessTravellerOfferCreditsTextLinkSwitch
                    type="exact"
                    offer={offer}
                    totalCost={baseTotalPrices.original.flightBundlePrice}
                    numberOfNights={hotelNights}
                    roomCount={rooms.length}
                  />
                </Group>
                {taxesAndFees > 0 && <PriceRowTaxesAndFees
                  endIcon={<OfferPriceDetailsRow
                    offerPage
                    offer={offer}
                    rooms={rooms}
                    propertyFees={bundlePropertyFees}
                    hotelPrice={bundlePrice}
                    hotelMemberPrice={bundleMemberPrice}
                    taxesAndFees={bundleTaxesAndFees}
                    flightPrice={flightPrice}
                    showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                    duration={hotelNights ?? 0}
                    trigger="info-icon"
                    cta={<TextButton kind="primary" fit="flex">
                      View {config.brandConfig.travelSynonym} options
                    </TextButton>}
                    onCtaAfterClose={onViewPackageOptions}
                  />}
                />}
                <PriceRowAgentHubCommission
                  size="L"
                  productType={offer.productType}
                  offerLocations={offerLocations}
                  offerId={offer.id}
                />
                {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
                  size="L"
                  originalValue={totalPrices.totals.flightBundleValue}
                />}
              </Group>
            </Group>
          </Group>
          <TextButton
            kind="secondary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            View options
          </TextButton>
        </Group>
      </AccommodationPackageTilePricingBlocks>
      <LoyaltyPointsEarn horizontalAlign="center" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
    </Group>
  }

  return (
    <Group direction="vertical" horizontalAlign={alignment}>
      <Group direction="vertical" horizontalAlign={alignment} gap={2}>
        <Group direction="vertical" horizontalAlign={alignment} gap={12}>
          <Group direction="vertical" horizontalAlign={alignment}>
            <Group direction="vertical" horizontalAlign={alignment} gap={4}>
              <Group direction="horizontal" gap={4}>
                {canPayLater && <PayLaterPill />}
                {displayDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
                  type="estimate"
                  discountPercentage={displayDiscount}
                />}
              </Group>
              <Group direction="vertical" horizontalAlign={alignment}>
                {!!nightsToDisplay && <PriceRowPriceCaption>
                  {pluralizeToString('night', nightsToDisplay)}{promoteAsBundle && ' + flights'} from
                </PriceRowPriceCaption>}
                <LuxPlusPriceGuard
                  showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                  fallbackPrice={<PriceRowPrice
                    data-testid="bedbank-hotel-summary-price"
                    size="L"
                    price={promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price}
                    saleUnit={saleUnit}
                  />}
                >
                  <LuxPlusPriceStack
                    horizontalAlign={alignment}
                    size="L"
                    price={promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price}
                    memberPrice={promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice}
                    saleUnit={saleUnit}
                  />
                </LuxPlusPriceGuard>
                {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              </Group>
              <BusinessTravellerOfferCreditsTextLinkSwitch
                type="exact"
                offer={offer}
                totalCost={promoteAsBundle ? baseTotalPrices.original.flightBundlePrice : baseTotalPrices.original.price}
                numberOfNights={hotelNights}
                roomCount={rooms.length}
              />
            </Group>
            {taxesAndFees > 0 && <PriceRowTaxesAndFees
              endIcon={<OfferPriceDetailsRow
                offerPage
                offer={offer}
                rooms={rooms}
                propertyFees={promoteAsBundle ? bundlePropertyFees : propertyFees}
                hotelPrice={promoteAsBundle ? bundlePrice : hotelPrice}
                hotelMemberPrice={promoteAsBundle ? bundleMemberPrice : hotelMemberPrice}
                taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
                flightPrice={promoteAsBundle ? flightPrice : undefined}
                showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                duration={hotelNights ?? 0}
                trigger="info-icon"
                cta={<TextButton kind="primary" fit="flex">
                  View {config.brandConfig.travelSynonym} options
                </TextButton>}
                onCtaAfterClose={onViewPackageOptions}
              />}
            />}
            <PriceRowAgentHubCommission
              size="L"
              productType={offer.productType}
              offerLocations={offerLocations}
              offerId={offer.id}
            />
            {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
              size="L"
              originalValue={promoteAsBundle ? totalPrices.totals.flightBundleValue : totalPrices.totals.value}
            />}
          </Group>
          <LoyaltyPointsEarn horizontalAlign="center" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
        </Group>
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
}), BedbankSummaryPriceDetails)
